import { createTheme } from '@mantine/core';
import type { MantineColorsTuple } from '@mantine/core';
import { generateColors } from '@mantine/colors-generator';

/**
 * Mantine theme configuration
 * Uses Mantine v8's createTheme API with CSS modules
 *
 * Features:
 * - Proper dark mode support with optimized colors
 * - Mobile-friendly: Base font size 16px to prevent iOS zoom
 * - Consistent component sizing across all components
 * - Configurable spacing for compactness control
 */

// Generate brand colors with proper dark mode support
const brandColors: MantineColorsTuple = generateColors('#375EAC');

export const theme = createTheme({
  /** Color scheme */
  primaryColor: 'brand',

  /** Custom colors */
  colors: {
    brand: brandColors,
    // Use Mantine's default dark colors - they're well-tested and optimized
    // No need to override unless you have specific brand requirements
  },

  /** White and black for dark mode */
  white: '#ffffff',
  black: '#000000',

  /** Font configuration */
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace:
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',

  headings: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '2rem', lineHeight: '1.3' },
      h2: { fontSize: '1.5rem', lineHeight: '1.35' },
      h3: { fontSize: '1.25rem', lineHeight: '1.4' },
      h4: { fontSize: '1.125rem', lineHeight: '1.45' },
      h5: { fontSize: '1rem', lineHeight: '1.5' },
      h6: { fontSize: '0.875rem', lineHeight: '1.5' },
    },
  },

  /** Font sizes - 16px base to prevent iOS zoom */
  fontSizes: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    md: '1rem', // 16px (base)
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
  },

  /** Line heights */
  lineHeights: {
    xs: '1.4',
    sm: '1.45',
    md: '1.55',
    lg: '1.6',
    xl: '1.65',
  },

  /** Spacing - can be adjusted for compactness */
  spacing: {
    xs: '0.625rem', // 10px
    sm: '0.75rem', // 12px
    md: '1rem', // 16px
    lg: '1.25rem', // 20px
    xl: '1.5rem', // 24px
  },

  /** Border radius */
  radius: {
    xs: '0.125rem', // 2px
    sm: '0.25rem', // 4px
    md: '0.5rem', // 8px
    lg: '0.75rem', // 12px
    xl: '1rem', // 16px
  },

  /** Default radius for components */
  defaultRadius: 'md',

  /** Shadows */
  shadows: {
    xs: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0px 10px 15px -5px, rgba(0, 0, 0, 0.04) 0px 7px 7px -5px',
    md: '0 1px 3px rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px',
    lg: '0 1px 3px rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0px 28px 23px -7px, rgba(0, 0, 0, 0.04) 0px 12px 12px -7px',
    xl: '0 1px 3px rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0px 36px 28px -7px, rgba(0, 0, 0, 0.04) 0px 17px 17px -7px',
  },

  /** Focus ring */
  focusRing: 'auto',

  /** Cursor type */
  cursorType: 'pointer',

  /** Auto contrast for better text visibility */
  autoContrast: true,
  luminanceThreshold: 0.3,

  /** Component-specific overrides - ensures consistent sizing */
  components: {
    // Form inputs
    TextInput: {
      defaultProps: {
        size: 'md',
      },
    },
    PasswordInput: {
      defaultProps: {
        size: 'md',
      },
    },
    Textarea: {
      defaultProps: {
        size: 'md',
      },
    },
    Select: {
      defaultProps: {
        size: 'md',
      },
    },
    MultiSelect: {
      defaultProps: {
        size: 'md',
      },
    },
    NumberInput: {
      defaultProps: {
        size: 'md',
      },
    },
    Checkbox: {
      defaultProps: {
        size: 'md',
      },
    },
    Radio: {
      defaultProps: {
        size: 'md',
      },
    },
    Switch: {
      defaultProps: {
        size: 'md',
      },
    },

    // Buttons and actions
    Button: {
      defaultProps: {
        size: 'md',
      },
    },
    ActionIcon: {
      defaultProps: {
        size: 'lg',
      },
    },
    UnstyledButton: {
      defaultProps: {
        // Ensure proper touch targets
      },
    },

    // Navigation
    Tabs: {
      defaultProps: {
        radius: 'md',
      },
    },
    NavLink: {
      defaultProps: {
        // Consistent styling
      },
    },

    // Overlays
    Modal: {
      defaultProps: {
        radius: 'md',
        padding: 'lg',
      },
    },
    Drawer: {
      defaultProps: {
        radius: 'md',
        padding: 'lg',
      },
    },
    Popover: {
      defaultProps: {
        radius: 'md',
        shadow: 'md',
      },
    },
    Menu: {
      defaultProps: {
        radius: 'md',
        shadow: 'md',
      },
    },
    Tooltip: {
      defaultProps: {
        radius: 'sm',
      },
    },

    // Data display
    Table: {
      defaultProps: {
        // Consistent table styling
      },
    },
    Card: {
      defaultProps: {
        radius: 'md',
        padding: 'lg',
      },
    },
    Paper: {
      defaultProps: {
        radius: 'md',
        p: 'md',
      },
    },
    Badge: {
      defaultProps: {
        size: 'md',
        radius: 'sm',
      },
    },
    Avatar: {
      defaultProps: {
        size: 'md',
        radius: 'xl',
      },
    },

    // Feedback
    Alert: {
      defaultProps: {
        radius: 'md',
      },
    },
    Notification: {
      defaultProps: {
        radius: 'md',
      },
    },
    Loader: {
      defaultProps: {
        size: 'md',
      },
    },
    Progress: {
      defaultProps: {
        size: 'md',
        radius: 'xl',
      },
    },

    // Layout
    AppShell: {
      defaultProps: {
        padding: 'md',
      },
    },
    Container: {
      defaultProps: {
        // Consistent container sizing
      },
    },
  },
});
